import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "./AuthContext";

interface Notification {
  id: string;
  message: string;
  type:
    | "message"
    | "mention"
    | "like"
    | "reply"
    | "system"
    | "topic"
    | "class_update"
    | "enrollment"
    | "course_announcement"
    | "assignment"
    | "grade"
    | "discussion";
  read: boolean;
  created_at: string;
  user_id: string;
  related_id?: string; // ID of related post, message, etc.
  related_type?: string; // Type of related content
  sender_id?: string; // ID of user who triggered the notification
  sender_name?: string; // Name of user who triggered the notification
  course_id?: string; // ID of related course
  course_name?: string; // Name of related course
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  addNotification: (
    notification: Omit<Notification, "id" | "created_at">,
  ) => void;
  requestPushPermission: () => Promise<boolean>;
  sendPushNotification: (title: string, body: string) => void;
  showInAppNotification: (message: string, type?: string, onClick?: () => void) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushPermission, setPushPermission] =
    useState<NotificationPermission>("default");

  useEffect(() => {
    if (!user) return;

    // Check push notification permission
    if ("Notification" in window) {
      setPushPermission(Notification.permission);
    }

    // Fetch existing notifications
    fetchNotifications();

    // Subscribe to real-time notifications
    const notificationsSubscription = supabase
      .channel(`notifications-${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications" },
        (payload) => {
          if (payload.new.user_id === user.id) {
            const newNotification = payload.new as Notification;
            setNotifications((prev) => [newNotification, ...prev]);
            updateUnreadCount([newNotification, ...notifications]);

            // Show in-app notification
            showInAppNotification(
              newNotification.message,
              newNotification.type,
            );

            // Send push notification if permitted
            if (pushPermission === "granted") {
              sendPushNotification("New Notification", newNotification.message);
            }

            // Play notification sound
            playNotificationSound();
          }
        },
      )
      .subscribe();

    // Subscribe to mentions in posts
    const mentionsSubscription = supabase
      .channel(`mentions-${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "posts" },
        (payload) => {
          const post = payload.new;
          if (
            post.content &&
            post.content.includes(`@${user.user_metadata?.username}`)
          ) {
            const mentionNotification: Omit<Notification, "id" | "created_at"> =
              {
                message: `${post.profiles?.full_name || "Someone"} mentioned you in a post`,
                type: "mention",
                read: false,
                user_id: user.id,
                related_id: post.id,
                related_type: "post",
                sender_id: post.user_id,
                sender_name: post.profiles?.full_name,
              };
            addNotification(mentionNotification);
          }
        },
      )
      .subscribe();

    // Subscribe to post reactions (including likes)
    const postReactionsSubscription = supabase
      .channel(`post_reactions-${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "post_reactions" },
        async (payload) => {
          const reaction = payload.new;

          // Only handle 'like' reactions for notifications
          if (reaction.reaction_type !== 'like') return;

          // Get the post details to find the post author
          const { data: postData, error: postError } = await supabase
            .from("posts")
            .select("user_id, content")
            .eq("id", reaction.post_id)
            .single();

          if (postError || !postData) {
            console.error(
              "Error fetching post for like notification:",
              postError,
            );
            return;
          }

          // Only notify the post author (not the person who liked)
          if (postData.user_id === user.id && reaction.user_id !== user.id) {
            // Get the liker's profile
            const { data: likerProfile, error: likerError } = await supabase
              .from("profiles")
              .select("full_name")
              .eq("id", reaction.user_id)
              .single();

            const likerName = likerProfile?.full_name || "Someone";

            const likeNotification: Omit<Notification, "id" | "created_at"> = {
              message: `${likerName} liked your post`,
              type: "like",
              read: false,
              user_id: user.id,
              related_id: reaction.post_id,
              related_type: "post",
              sender_id: reaction.user_id,
              sender_name: likerName,
            };
            addNotification(likeNotification);
          }
        },
      )
      .subscribe();

    // Subscribe to course updates and enrollments
    const courseSubscription = supabase
      .channel(`courses-${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "enrollments" },
        (payload) => {
          const enrollment = payload.new;
          if (enrollment.user_id === user.id) {
            const enrollmentNotification: Omit<Notification, "id" | "created_at"> =
              {
                message: `You've been enrolled in a new course!`,
                type: "enrollment",
                read: false,
                user_id: user.id,
                related_id: enrollment.course_id,
                related_type: "course",
                sender_id: enrollment.user_id,
                sender_name: "System",
              };
            addNotification(enrollmentNotification);
          }
        },
      )
      .subscribe();

    // Subscribe to messages
    const messagesSubscription = supabase
      .channel(`messages-${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages" },
        (payload) => {
          const message = payload.new;
          if (message.recipient_id === user.id) {
            const messageNotification: Omit<Notification, "id" | "created_at"> = {
              message: `You have a new message from ${message.sender_name || "Someone"}`,
              type: "message",
              read: false,
              user_id: user.id,
              related_id: message.id,
              related_type: "message",
              sender_id: message.sender_id,
              sender_name: message.sender_name,
            };
            addNotification(messageNotification);
          }
        },
      )
      .subscribe();

    // Subscribe to replies
    const repliesSubscription = supabase
      .channel(`replies-${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "replies" },
        async (payload) => {
          const reply = payload.new;

          // Get the original post to find the post author
          const { data: postData, error: postError } = await supabase
            .from("posts")
            .select("user_id, content")
            .eq("id", reply.post_id)
            .single();

          if (postError || !postData) {
            console.error("Error fetching post for reply notification:", postError);
            return;
          }

          // Only notify the post author (not the person who replied)
          if (postData.user_id === user.id && reply.user_id !== user.id) {
            // Get the replier's profile
            const { data: replierProfile, error: replierError } = await supabase
              .from("profiles")
              .select("full_name")
              .eq("id", reply.user_id)
              .single();

            const replierName = replierProfile?.full_name || "Someone";

            const replyNotification: Omit<Notification, "id" | "created_at"> = {
              message: `${replierName} replied to your post`,
              type: "reply",
              read: false,
              user_id: user.id,
              related_id: reply.post_id,
              related_type: "post",
              sender_id: reply.user_id,
              sender_name: replierName,
            };
            addNotification(replyNotification);
          }
        },
      )
      .subscribe();

    // Subscribe to reply likes
    const replyLikesSubscription = supabase
      .channel(`reply_likes-${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "reply_likes" },
        async (payload) => {
          const like = payload.new;

          // Get the reply to find the reply author
          const { data: replyData, error: replyError } = await supabase
            .from("replies")
            .select("user_id, content")
            .eq("id", like.reply_id)
            .single();

          if (replyError || !replyData) {
            console.error("Error fetching reply for like notification:", replyError);
            return;
          }

          // Only notify the reply author (not the person who liked)
          if (replyData.user_id === user.id && like.user_id !== user.id) {
            // Get the liker's profile
            const { data: likerProfile, error: likerError } = await supabase
              .from("profiles")
              .select("full_name")
              .eq("id", like.user_id)
              .single();

            const likerName = likerProfile?.full_name || "Someone";

            const likeNotification: Omit<Notification, "id" | "created_at"> = {
              message: `${likerName} liked your reply`,
              type: "like",
              read: false,
              user_id: user.id,
              related_id: like.reply_id,
              related_type: "reply",
              sender_id: like.user_id,
              sender_name: likerName,
            };
            addNotification(likeNotification);
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(notificationsSubscription);
      supabase.removeChannel(mentionsSubscription);
      supabase.removeChannel(postReactionsSubscription);
      supabase.removeChannel(courseSubscription);
      supabase.removeChannel(messagesSubscription);
      supabase.removeChannel(repliesSubscription);
      supabase.removeChannel(replyLikesSubscription);
    };
  }, [user, pushPermission]);

  const fetchNotifications = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      setNotifications(data || []);
      updateUnreadCount(data || []);
    } catch (error) {
      console.error(
        "Error fetching notifications:",
        error instanceof Error ? error.message : String(error),
      );
    }
  };

  const updateUnreadCount = (notifs: Notification[]) => {
    setUnreadCount(notifs.filter((n) => !n.read).length);
  };

  const markAsRead = async (id: string) => {
    try {
      const { error } = await supabase
        .from("notifications")
        .update({ read: true })
        .eq("id", id);

      if (error) throw error;
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
      );
      updateUnreadCount(
        notifications.map((n) => (n.id === id ? { ...n, read: true } : n)),
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("notifications")
        .update({ read: true })
        .eq("user_id", user.id)
        .eq("read", false);

      if (error) throw error;
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const addNotification = async (
    notification: Omit<Notification, "id" | "created_at">,
  ) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("notifications")
        .insert([
          {
            user_id: user.id,
            message: notification.message,
            type: notification.type,
            read: notification.read,
            related_id: notification.related_id,
            related_type: notification.related_type,
            sender_id: notification.sender_id,
            sender_name: notification.sender_name,
          },
        ])
        .select()
        .single();

      if (error) throw error;
      setNotifications((prev) => [data, ...prev]);
      updateUnreadCount([data, ...notifications]);
    } catch (error) {
      console.error("Error adding notification:", error);
    }
  };

  const requestPushPermission = async (): Promise<boolean> => {
    if (!("Notification" in window)) {
      console.log("This browser does not support notifications");
      return false;
    }

    if (Notification.permission === "granted") {
      return true;
    }

    if (Notification.permission === "denied") {
      console.log("Notification permission denied");
      return false;
    }

    const permission = await Notification.requestPermission();
    setPushPermission(permission);
    return permission === "granted";
  };

  const sendPushNotification = (title: string, body: string) => {
    if (pushPermission === "granted") {
      new Notification(title, {
        body,
        icon: "/ascend-logo.png",
        badge: "/ascend-logo.png",
        tag: "ascend-notification",
      });
    }
  };

  const showInAppNotification = (message: string, type: string = "info", onClick?: () => void) => {
    // Create a temporary in-app notification
    const notificationElement = document.createElement("div");
    notificationElement.className = `fixed top-20 right-4 z-50 p-4 rounded-lg shadow-lg max-w-sm transform transition-all duration-300 translate-x-full cursor-pointer`;

    // Style based on type
    switch (type) {
      case "mention":
        notificationElement.className += " bg-blue-500 text-white";
        break;
      case "like":
        notificationElement.className += " bg-red-500 text-white";
        break;
      case "reply":
        notificationElement.className += " bg-green-500 text-white";
        break;
      case "message":
        notificationElement.className += " bg-purple-500 text-white";
        break;
      case "class_update":
        notificationElement.className += " bg-yellow-500 text-white";
        break;
      case "enrollment":
        notificationElement.className += " bg-indigo-500 text-white";
        break;
      case "course_announcement":
        notificationElement.className += " bg-orange-500 text-white";
        break;
      case "assignment":
        notificationElement.className += " bg-teal-500 text-white";
        break;
      case "grade":
        notificationElement.className += " bg-pink-500 text-white";
        break;
      case "discussion":
        notificationElement.className += " bg-cyan-500 text-white";
        break;
      case "topic":
        notificationElement.className += " bg-emerald-500 text-white";
        break;
      default:
        notificationElement.className += " bg-gray-800 text-white";
    }

    notificationElement.innerHTML = `
      <div class="flex items-center space-x-3">
        <span class="text-sm flex-1">${message}</span>
        <button class="text-white hover:text-gray-200" data-close>
          ×
        </button>
      </div>
    `;

    document.body.appendChild(notificationElement);

    // Close button handler
    const closeBtn = notificationElement.querySelector('[data-close]');
    closeBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      notificationElement.remove();
    });

    // Main click handler (trigger action)
    notificationElement.addEventListener('click', () => {
      try {
        onClick && onClick();
      } finally {
        if (notificationElement.parentElement) notificationElement.remove();
      }
    });

    // Animate in
    setTimeout(() => {
      notificationElement.classList.remove("translate-x-full");
    }, 100);

    // Auto remove after 5 seconds
    setTimeout(() => {
      if (notificationElement.parentElement) {
        notificationElement.classList.add("translate-x-full");
        setTimeout(() => {
          if (notificationElement.parentElement) {
            notificationElement.remove();
          }
        }, 300);
      }
    }, 5000);
  };

  const playNotificationSound = () => {
    const audio = new Audio("/notification.mp3");
    audio.play().catch((error) => {
      console.error("Error playing notification sound:", error);
    });
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        addNotification,
        requestPushPermission,
        sendPushNotification,
        showInAppNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider",
    );
  }
  return context;
};
